import React, { useEffect, useRef } from "react";

/**
 * Full-bleed image that drifts slower than the page.
 *
 * The inner image is over-scaled so the translation never exposes an edge.
 * Only listens to scroll while the frame is actually on screen, and opts out
 * entirely when motion is reduced.
 */
const Parallax = ({ src, alt = "", strength = 14, className = "", children }) => {
  const frameRef = useRef(null);
  const imgRef = useRef(null);

  useEffect(() => {
    const frame = frameRef.current;
    const img = imgRef.current;
    if (!frame || !img) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let ticking = false;
    let active = false;

    const update = () => {
      const rect = frame.getBoundingClientRect();
      // -1 when the frame is just below the fold, +1 when just above it
      const progress =
        (rect.top + rect.height / 2 - window.innerHeight / 2) /
        (window.innerHeight / 2 + rect.height / 2);
      img.style.transform = `translate3d(0, ${(progress * strength).toFixed(2)}%, 0) scale(1.18)`;
      ticking = false;
    };

    const onScroll = () => {
      if (ticking || !active) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        active = entry.isIntersecting;
        if (active) update();
      },
      { threshold: 0 }
    );
    io.observe(frame);

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      io.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [strength]);

  return (
    <div className={`parallax ${className}`.trim()} ref={frameRef}>
      <img ref={imgRef} src={src} alt={alt} className="parallax__img" />
      {children}
    </div>
  );
};

export default Parallax;
